import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { updateShowOtaConfirmBackupProcessModal } from "@src/reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaBackupProcessReviewRequestAndApproveProps {
  activeInstallationData?: ActiveInstallationProcessPayload | null;
}

export const useOtaBackupProcessReviewRequestAndApprove = ({
  activeInstallationData,
}: UseOtaBackupProcessReviewRequestAndApproveProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [initiatorName, setInitiatorName] = useState<string | null>(null);
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const texts = {
    title: i18n("modules.otaBackupProcessReviewRequestAndApprove.title"),
    descriptionTemplate: i18n(
      "modules.otaBackupProcessReviewRequestAndApprove.descriptionTemplate",
    ),
    reviewAndApproveButtonText: i18n(
      "modules.otaBackupProcessReviewRequestAndApprove.reviewAndApproveButtonText",
    ),
  };

  const handleReviewRequestAndApprove = () => {
    dispatch(updateShowOtaConfirmBackupProcessModal(true));
  };

  useEffect(() => {
    async function fetchInitiatorName() {
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Fetching backup request initiator details for ID:",
          activeInstallationData?.initiator_id,
        );

      const response = await getSingleUserUseCase(
        activeInstallationData?.initiator_id || "",
      );

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from getSingleUserUseCase (backup initiator):",
          response,
        );

      if (response.success) {
        setInitiatorName(response.data?.display_name || "");
      } else {
        setInitiatorName("");
      }
    }

    fetchInitiatorName();
  }, [activeInstallationData?.initiator_id, isDebugLoggingEnabled]);

  const descriptionText = texts.descriptionTemplate.replace(
    "{initiator}",
    initiatorName || "",
  );

  return {
    texts,
    descriptionText,
    showContent: initiatorName !== null,
    handleReviewRequestAndApprove,
  };
};
